import { ref, computed } from 'vue'
import { useAuthStore } from '../stores/AuthStore'
import { useSessionStorageStore } from '../stores/SessionStorage'
import { hasPermission, isPlatformAdmin } from '../access/hasPermission'
import { PERMISSION_KEYS } from '../access/permissionKeys'

// Admin "view-as" context for the Transform module: while active, every Transform view
// presents the `ownerUid` user's data instead of the authenticated user's. This is the
// single frontend source of truth for that state — navigation params only SEED it (via
// NavigationStore.navigate, the one choke point all Transform navigation goes through);
// views and the TransformLearningStore read from here, never from navParams.owner.
//
// Authorization is enforced server-side (resolveResultsTargetUser): a platform admin may
// target anyone, an agency admin (adminCenter.access) only a user of their own agency, and
// anyone else just gets their own data back. The permission pre-gate here is UX, not
// security — it cannot tell agencies apart, so the server's answer (ownerResolved) is final.
//
// Module-level refs (not a Pinia store): the context is a singleton by construction and
// must be importable from stores without adding another store-to-store dependency.
const ownerUid = ref(null)
const targetUser = ref(null)

// sessionStorage event that survives a full page refresh; the HomePage F5-restore table
// re-injects it as the `owner` navigation param, which re-enters the context.
const OWNER_EVENT = 'transformOwner'

function canViewOtherUsers(effectivePermissionKeys) {
  return isPlatformAdmin(effectivePermissionKeys) ||
    hasPermission(effectivePermissionKeys, PERMISSION_KEYS.ADMIN_CENTER_ACCESS)
}

export function useTransformOwnerContext() {
  const isActive = computed(() => !!ownerUid.value)

  // Activates the context for `uid`. No-ops (and clears any stale context) when the
  // caller cannot view other users or is targeting themselves — mirroring the server's
  // resolution rules so the UI never claims a context the backend won't honor.
  function enter(uid) {
    const authStore = useAuthStore()
    const selfUid = localStorage.getItem('userUid')
    const requested = typeof uid === 'string' ? uid.trim() : ''
    if (!requested || requested === selfUid || !canViewOtherUsers(authStore.effectivePermissionKeys)) {
      exit()
      return
    }
    if (ownerUid.value !== requested) {
      ownerUid.value = requested
      targetUser.value = null
    }
    useSessionStorageStore().setEvent(OWNER_EVENT, requested)
  }

  function exit() {
    ownerUid.value = null
    targetUser.value = null
    useSessionStorageStore().cleanEvent(OWNER_EVENT)
  }

  // The target's display name comes from gateway responses (getCourseProgress /
  // getQuizResultBreakdown return `targetUser`); whoever fetches it feeds the banner here.
  function setTargetUser(user) {
    if (isActive.value && user?.id === ownerUid.value) targetUser.value = user
  }

  const targetUserName = computed(() => targetUser.value?.name?.trim() || 'another user')

  return { ownerUid, targetUser, targetUserName, isActive, enter, exit, setTargetUser }
}
